import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, Mail, Phone, MessageCircle, Clock, CreditCard, Headphones } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { useCountry } from '../hooks/useCountry';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/ui/Button';
export function AccountSuspendedPage() {
  const {
    language,
    t
  } = useLanguage();
  const {
    whatsappNumber,
    phoneNumber
  } = useCountry();
  const {
    user
  } = useAuth();
  const isRTL = language === 'ar';
  // Get contact methods based on user's country
  const getContactMethods = () => {
    const userCountry = user?.country || 'eg';
    const allContacts = {
      eg: [{
        icon: Mail,
        label: t('suspended.email'),
        value: 'hello@operines.ai',
        href: 'mailto:hello@operines.ai',
        color: 'bg-blue-500'
      }, {
        icon: Phone,
        label: isRTL ? 'الهاتف' : 'Phone',
        value: '+20 150 808 7501',
        href: 'tel:+201508087501',
        color: 'bg-green-500'
      }, {
        icon: MessageCircle,
        label: isRTL ? 'واتساب' : 'WhatsApp',
        value: '+20 150 808 7501',
        href: 'https://wa.me/201508087501',
        color: 'bg-emerald-500'
      }],
      ae: [{
        icon: Mail,
        label: t('suspended.email'),
        value: 'hello@operines.ai',
        href: 'mailto:hello@operines.ai',
        color: 'bg-blue-500'
      }, {
        icon: Phone,
        label: isRTL ? 'الهاتف' : 'Phone',
        value: '+971 50 854 6970',
        href: 'tel:+971508546970',
        color: 'bg-green-500'
      }, {
        icon: MessageCircle,
        label: isRTL ? 'واتساب' : 'WhatsApp',
        value: '+971 50 854 6970',
        href: 'https://wa.me/971508546970',
        color: 'bg-emerald-500'
      }],
      sa: [{
        icon: Mail,
        label: t('suspended.email'),
        value: 'hello@operines.ai',
        href: 'mailto:hello@operines.ai',
        color: 'bg-blue-500'
      }, {
        icon: Phone,
        label: isRTL ? 'الهاتف' : 'Phone',
        value: '+971 50 854 6970',
        href: 'tel:+971508546970',
        color: 'bg-green-500'
      }, {
        icon: MessageCircle,
        label: isRTL ? 'واتساب' : 'WhatsApp',
        value: '+971 50 854 6970',
        href: 'https://wa.me/971508546970',
        color: 'bg-emerald-500'
      }]
    };
    return allContacts[userCountry as keyof typeof allContacts] || allContacts.eg;
  };
  const contactMethods = getContactMethods();
  return <div className="min-h-screen bg-gradient-to-b from-red-50 via-white to-gray-50 flex items-center justify-center p-4 sm:p-6" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-723">
      <motion.div initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.5
    }} className="max-w-lg w-full" data-id="element-724">
        {/* Main Card */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden" data-id="element-725">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-500 to-orange-500 p-6 sm:p-8 text-center" data-id="element-726">
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.2,
            type: 'spring'
          }} className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-727">
              <motion.div animate={{
              rotate: [0, 10, -10, 0]
            }} transition={{
              duration: 2,
              repeat: Infinity
            }} data-id="element-728">
                <AlertTriangle className="w-8 h-8 sm:w-10 sm:h-10 text-white" data-id="element-729" />
              </motion.div>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2" data-id="element-730">
              {t('suspended.title')}
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="text-white/90 text-sm sm:text-base" data-id="element-731">
              {t('suspended.subtitle')}
            </motion.p>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 md:p-8" data-id="element-732">
            {/* Trial Expired Notice */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-amber-50 border border-amber-200 rounded-xl mb-6" data-id="element-733">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-734">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" data-id="element-735" />
              </div>
              <div data-id="element-736">
                <h3 className="font-semibold text-amber-800 text-sm sm:text-base" data-id="element-737">
                  {t('suspended.trialEnded')}
                </h3>
                <p className="text-xs sm:text-sm text-amber-700" data-id="element-738">
                  {t('suspended.subscribeMessage')}
                </p>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.55
          }} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-id="element-739">
              <Link to="/subscription" className="w-full" data-id="element-740">
                <motion.div whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} data-id="element-741">
                  <Button fullWidth size="lg" className="bg-gradient-to-r from-[#4f1964] to-purple-600 hover:from-[#3d1350] hover:to-purple-700 text-white shadow-lg" data-id="element-742">
                    <CreditCard className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-743" />
                    {t('dashboard.subscribeNow')}
                  </Button>
                </motion.div>
              </Link>

              <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="w-full" data-id="element-744">
                <motion.div whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} data-id="element-745">
                  <Button fullWidth size="lg" variant="outline" className="border-green-500 text-green-600 hover:bg-green-50" data-id="element-746">
                    <Headphones className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-747" />
                    {t('suspended.contactSupport')}
                  </Button>
                </motion.div>
              </a>
            </motion.div>

            {/* Message */}
            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.6
          }} className="text-gray-600 text-center mb-6 text-sm sm:text-base" data-id="element-748">
              {t('suspended.contactMessage')}
            </motion.p>

            {/* Contact Methods */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.7
          }} className="space-y-2 sm:space-y-3" data-id="element-749">
              <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-center text-sm sm:text-base" data-id="element-750">
                {t('suspended.contactMethods')}
              </h3>

              {contactMethods.map((method, index) => <motion.a key={index} href={method.href} target="_blank" rel="noopener noreferrer" initial={{
              opacity: 0,
              x: isRTL ? 20 : -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.8 + index * 0.1
            }} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group" data-id="element-751">
                  <div className={`w-9 h-9 sm:w-10 sm:h-10 ${method.color} rounded-lg flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`} data-id="element-752">
                    <method.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" data-id="element-753" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-754">
                    <p className="text-xs sm:text-sm text-gray-500" data-id="element-755">
                      {method.label}
                    </p>
                    <p className="font-medium text-gray-900 text-sm sm:text-base truncate" data-id="element-756">
                      {method.value}
                    </p>
                  </div>
                  <motion.span className="text-gray-400 group-hover:text-[#4f1964] text-sm" animate={{
                x: [0, 5, 0]
              }} transition={{
                duration: 1.5,
                repeat: Infinity
              }} data-id="element-757">
                    {isRTL ? '←' : '→'}
                  </motion.span>
                </motion.a>)}
            </motion.div>
          </div>
        </div>

        {/* Footer */}
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.2
      }} className="text-center text-gray-500 text-xs sm:text-sm mt-4 sm:mt-6" data-id="element-758">
          {t('suspended.thanks')}
        </motion.p>
      </motion.div>
    </div>;
}